import React, { useState, useEffect } from 'react'
import { AxiosError } from 'axios'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  InputLabel,
  TextField,
  Autocomplete,
  MenuItem,
  Alert,
  Snackbar,
  ListSubheader
} from '@mui/material'
import { useForm, FormProvider, useWatch, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  CreativePreviewResponse,
  AspectRatio,
  Flight,
  DurationInput,
  DurationInputSchema,
  CreativeUploadResponse,
  CreativeUpdateInput,
  ErrorResponse,
  CampaignStatus,
  FileUploadFields,
  ContentCategory
} from '../../types/campaign'
import { DATE_STRING, CREATIVE_TIMEFRAME_WARNING, zoneAbbrs } from '../../types/constants'
import { pacificDateStringToUtcStartOfDay, pacificDateStringToUtcEndOfDay } from '../utils'
import AppModal from '../AppModal'
import FlightDates from '../FlightDates'
import AssetUploader from '../AssetUploader'
import InputErrorMessage from '../InputErrorMessage'
import CreativeOptionHeader from '../guidelines/CreativeOptionHeader'
import CreativeDetails from '../CreativeDetails'
import theme from '../theme'
import { deleteCreative, updateCreative, uploadCampaignVideo, fetchContentCategories } from '../getData'
import { getFormatsToRender, getCreativeStatus, getParentCategoryId, getParentCategoryName } from '../utils'
import ConfirmationModal from '../ConfirmationModal'
import CreativeTypeSelector from '../CreativeTypeSelector'
import ContentCategoryMismatchBadge from '../guidelines/ContentCategoryMismatchBadge'

interface Props {
  creativeFormats: CreativePreviewResponse | null
  creativeAssets: CreativeUploadResponse[]
  isPending: boolean
  isError: boolean
  flight: Flight
  isFlightAccordion: boolean
  campaignStatus: CampaignStatus
  saveTargetsMutation?: () => void
  warningMessage?: string
  campaignContentCategory?: ContentCategory | null
  isGtvUser: boolean
}

const FlightCreative = ({
  creativeFormats,
  isPending,
  isError,
  flight,
  isFlightAccordion,
  saveTargetsMutation,
  warningMessage,
  campaignStatus,
  campaignContentCategory,
  isGtvUser
}: Props) => {
  const canDeleteCreative = (creative: CreativeUploadResponse): boolean => {
    const creativeStatus = getCreativeStatus(creative, flight, campaignStatus)
    return ![
      CampaignStatus.Live,
      CampaignStatus.Completed
    ].includes(creativeStatus)
  }
  const [creativeModal, setCreativeModal] = useState<{
    open: boolean
    creativeId: number | undefined
  }>({ open: false, creativeId: undefined })

  const [initialDuration, setInitialDuration] = useState<{
    starts_at: string | null
    ends_at: string | null
  }>({
    starts_at: null,
    ends_at: null
  })

  const [deleteModal, setDeleteModal] = useState<{
    open: boolean
    id: number
  }>({
    open: false,
    id: 0
  })

  const [uploadPending, setUploadPending] = useState(false)
  const [uploadError, setUploadError] = useState({
    error: false,
    errorMessage: ''
  })

  const [openSnackbar, setOpenSnackbar] = useState<{
    open: boolean
    message: string
  }>({ open: false, message: '' })

  const [selectedAspectRatio, setSelectedAspectRatio] =
    useState<AspectRatio | null>(null)

  const [creativeStatus, setCreativeStatus] = useState<{
    hasStarted: boolean
    hasEnded: boolean
  }>({ hasStarted: false, hasEnded: false })
  const [mediaType, setMediaType] = useState<'image' | 'video'>('video')
  const [selectedDuration, setSelectedDuration] = useState<number>(15)
  const [preparedFile, setPreparedFile] = useState<File | null>(null)
  const [preparedMediaLength, setPreparedMediaLength] = useState<number>(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [dateTouched, setDateTouched] = useState<{ startTouched: boolean; endTouched: boolean }>({ startTouched: false, endTouched: false })
  const [selectedContentCategoryId, setSelectedContentCategoryId] = useState<number | null>(null)
  const [userChangedContentCategory, setUserChangedContentCategory] = useState<boolean>(false)
  const [previewRef, setPreviewRef] = useState<HTMLDivElement | null>(null)
  const modalContentRef = React.useRef<HTMLDivElement>(null)

  // Common styles for media preview (image/video)
  const mediaPreviewStyles: React.CSSProperties = {
    maxWidth: '400px', // Maximum width for display
    maxHeight: '600px', // Maximum height for display
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block'
  }

  // Common button styles
  const buttonStyles = {
    width: {
      xs: '100%',  // Mobile: full width
      sm: '45%',   // Small screens: 45% width each
      md: '40%'    // Medium screens: 40% width each
    },
    height: '56px',
    borderRadius: theme.borders['rounded-10'],
    flex: {
      xs: 'none',  // Mobile: no flex
      sm: '1'      // Small screens and up: equal flex
    }
  }

  // Common TextField font sizes
  const textFieldFontSizes = {
    '& .MuiOutlinedInput-root': {
      fontSize: {
        xs: '14px', // Mobile: smaller font
        sm: '16px'  // Small screens and up: normal font
      }
    }
  }

  // Common typography color
  const labelTextColor = theme.palette['sage-900']

  // Common responsive spacing patterns
  const responsiveGaps = {
    xs: theme.space['16'], // Mobile: smaller gaps
    sm: theme.space['20'], // Small tablets: medium gaps
    md: theme.space['24']  // Medium screens and up: full gaps
  }

  const responsiveMargins = {
    xs: theme.space['16'], // Mobile: smaller margins
    sm: theme.space['24'], // Small tablets: medium margins
    md: theme.space['32']  // Medium screens and up: full margins
  }


  const handleSelectUpload = (aspectRatio: AspectRatio) => {
    setSelectedAspectRatio(aspectRatio)
    setDateTouched({ startTouched: false, endTouched: false })
    setCreativeModal({ open: true, creativeId: undefined })
  }

  const defaultTimeZone = () => {
    const LAPacific = 'America/Los_Angeles'
    const isDST = dayjs().tz(LAPacific).format('z') !== zoneAbbrs[LAPacific][0]
    const abbr = zoneAbbrs[LAPacific][isDST ? 1 : 0]

    return {
      label: `${LAPacific.replace('America/', '').replace(/_/g, ' ')} (${abbr})`,
      value: LAPacific
    }
  }

  const time_zone = defaultTimeZone().value

  const start = flight.duration.starts_at
    ? dayjs(flight.duration.starts_at).tz(time_zone).format(DATE_STRING)
    : ''

  const durationStart = dayjs(start).tz(time_zone).isBefore(dayjs().startOf('day'))
    ? dayjs().tz(time_zone).format(DATE_STRING)
    : start
  const durationEnd = flight.duration.ends_at
    ? dayjs(flight.duration.ends_at).tz(time_zone).format(DATE_STRING)
    : ''

  const formMethods = useForm<DurationInput>({
    mode: 'all',
    resolver: yupResolver(DurationInputSchema),
    defaultValues: {
      name: '',
      duration: {
        starts_at: null,
        ends_at: null
      }
    }
  })

  const { mutate: deleteCreativeMutation, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (creativeId: number) => deleteCreative(creativeId),
      onSuccess: () => {
        setDeleteModal({ open: false, id: 0 })
        setOpenSnackbar({
          open: true,
          message: 'Creative deleted successfully'
        })
        saveTargetsMutation && saveTargetsMutation()
        handleCloseModal()
      },
      onError: (error: AxiosError) => {
        setDeleteModal({ open: false, id: 0 })
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') ||
          'Error deleting creative'
        setOpenSnackbar({ open: true, message })
      }
    })

  const { mutate: updateCreativeMutation, isPending: isPendingUpdate } =
    useMutation({
      mutationFn: (updatedCreative: CreativeUpdateInput) =>
        updateCreative(updatedCreative),
      onSuccess: () => {
        setCreativeModal({ open: false, creativeId: undefined })
        setOpenSnackbar({
          open: true,
          message: 'Creative updated successfully'
        })
        saveTargetsMutation && saveTargetsMutation()
      },
      onError: (error: AxiosError) => {
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') ||
          'Error updating creative'
        setUploadError({ error: true, errorMessage: message })
      }
    })

  const { mutate: createCreativeMutation, isPending: isPendingCreate } =
    useMutation({
      mutationFn: async (params: {
        flightId: number
        fields: FileUploadFields
        file: File
      }) => uploadCampaignVideo(params.flightId, params.fields, params.file),
      onSuccess: () => {
        handleCloseModal()
        setOpenSnackbar({ open: true, message: 'Creative uploaded successfully' })
        saveTargetsMutation && saveTargetsMutation()
      },
      onError: (error: AxiosError) => {
        const errorResponse = error.response?.data as ErrorResponse
        const message =
          errorResponse?.context?.map((text) => text).join(', ') || 'Upload failed'
        setUploadError({ error: true, errorMessage: message })
      }
    })

  const { data: contentCategories } = useQuery({
    queryKey: ['contentCategories'],
    queryFn: fetchContentCategories,
    retry: false,
    enabled: isGtvUser
  })

  // Content category mismatch detection
  const selectedContentCategory = contentCategories?.find(c => c.id === selectedContentCategoryId)

  const childContentCategories = contentCategories?.filter((category) => category.parent_id !== null)

  const {
    clearErrors,
    formState: { errors },
    setValue,
    reset: resetForm
  } = formMethods

  // Watch for changes in duration values to pass reactive data to AssetUploader
  const watchedDuration = useWatch({
    control: formMethods.control,
    name: 'duration'
  })

  const watchedName = useWatch({
    control: formMethods.control,
    name: 'name'
  })

  const creativesOutsideFlightDuration = flight.creatives.some((creative) => {
    return dayjs(creative.calculated_duration.starts_at).isBefore(
      dayjs(flight.duration.starts_at)
    )
  })

  const handleCloseModal = () => {
    setCreativeModal({ open: false, creativeId: undefined })
    setUploadPending(false)
    setUploadError({ error: false, errorMessage: '' })
    setCreativeStatus({ hasStarted: false, hasEnded: false })
    setDateTouched({ startTouched: false, endTouched: false })
    setMediaType('video')
    resetForm({
      name: '',
      duration: {
        starts_at: null,
        ends_at: null
      }
    })
    setPreparedFile(null)
    setPreparedMediaLength(0)
    setSelectedContentCategoryId(null)
    setUserChangedContentCategory(false)
    setSelectedAspectRatio(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
      setPreviewUrl(null)
    }
  }

  const handleEditCreative = (creative: CreativeUploadResponse) => {
    const creativeStart = creative.calculated_duration.starts_at
    const creativeEnd = creative.calculated_duration.ends_at

    const creativeStartInTimezone = creativeStart
      ? dayjs(creativeStart).tz(time_zone).format(DATE_STRING)
      : durationStart

    const creativeEndInTimezone = creativeEnd
      ? dayjs(creativeEnd).tz(time_zone).format(DATE_STRING)
      : durationEnd

    // Set original values immediately to avoid one-render lag
    setInitialDuration({
      starts_at: creative.duration.starts_at,
      ends_at: creative.duration.ends_at
    })
    setDateTouched({ startTouched: false, endTouched: false })

    setCreativeModal({ open: true, creativeId: creative.id })
    setValue('name', creative.name)
    setValue('duration', {
      starts_at: creativeStartInTimezone,
      ends_at: creativeEndInTimezone
    })
    const selectedId = contentCategories?.find((c) => c.name === creative.content_category)?.id ?? null
    const fallbackId = campaignContentCategory?.id ?? null
    setSelectedContentCategoryId(selectedId ?? fallbackId)
    setCreativeStatus({
      hasStarted: dayjs(creativeStartInTimezone).isBefore(dayjs()),
      hasEnded: dayjs(creativeEndInTimezone).isBefore(dayjs())
    })
  }

  // Keep initialDuration in sync with the opened creative's original duration
  useEffect(() => {
    if (!creativeModal.open || !creativeModal.creativeId) return
    const current = flight.creatives.find((c) => c.id === creativeModal.creativeId)
    setInitialDuration({
      starts_at: current?.duration.starts_at ?? null,
      ends_at: current?.duration.ends_at ?? null
    })
  }, [creativeModal.open, creativeModal.creativeId, flight.creatives, campaignContentCategory])

  // On modal open (new or edit), default the content category if none selected and user hasn't changed it
  useEffect(() => {
    if (!creativeModal.open) return
    if (userChangedContentCategory) return

    const current = creativeModal.creativeId
      ? flight.creatives.find((c) => c.id === creativeModal.creativeId)
      : undefined

    let defaultId: number | null = null
    if (current?.content_category) {
      defaultId = contentCategories?.find((c) => c.name === current.content_category)?.id ?? null
    }
    if (defaultId == null) {
      defaultId = campaignContentCategory?.id ?? null
    }
    if (defaultId != null) setSelectedContentCategoryId((prev) => (prev == null ? defaultId : prev))
  }, [creativeModal.open, creativeModal.creativeId, flight.creatives, contentCategories, campaignContentCategory, userChangedContentCategory])

  useEffect(() => {
    if (previewUrl) {
      setTimeout(() => {
        if (previewRef) {
          previewRef.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        } else if (modalContentRef.current) {
          modalContentRef.current.scrollTo({
            top: modalContentRef.current.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 200) // Increased delay to ensure preview is fully rendered
    }
  }, [previewUrl, previewRef])

  const header = isFlightAccordion ? (
    <Typography
      variant="body-10"
      sx={{
        display: 'block',
        color: theme.palette['sage-400'],
        marginBottom: theme.space['24']
      }}
    >
      Creative Assets
    </Typography>
  ) : (
    <Typography
      variant="body-8"
      sx={{
        display: 'block',
        marginTop: theme.space['48'],
        marginBottom: theme.space['24']
      }}
    >
      Creative Formats
    </Typography>
  )

  if (isPending) {
    return (
      <Box>
        {header}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress sx={{ color: theme.palette['accent-500'] }} />
        </Box>
      </Box>
    )
  }
  if (isError) {
    return (
      <Box>
        {header}
        <Typography variant="body-10">Error loading creative</Typography>
      </Box>
    )
  }
  if (!creativeFormats) {
    return (
      <Box>
        {header}
        <Box
          sx={{
            padding: theme.space['24'],
            backgroundColor: theme.palette['sage-50'],
            borderRadius: theme.borders['rounded-10']
          }}
        >
          <Typography
            variant="body-11"
            sx={{ color: theme.palette['sage-500'] }}
          >
            Select zones to see a list of creative formats
          </Typography>
        </Box>
      </Box>
    )
  }

  const formatAlert = (
    <Alert
      severity="warning"
      sx={{
        marginBottom: theme.space['24'],
        borderRadius: theme.borders['rounded-10'],
        backgroundColor: theme.palette['orange-200'],
        color: theme.palette['orange-900']
      }}
    >
      {warningMessage}
    </Alert>
  )

  const timeframeAlert = (
    <Alert
      severity="warning"
      sx={{
        marginBottom: theme.space['24'],
        borderRadius: theme.borders['rounded-10'],
        backgroundColor: theme.palette['orange-200'],
        color: theme.palette['orange-900']
      }}
    >
      {CREATIVE_TIMEFRAME_WARNING}
    </Alert>
  )

  const formatsToRender = getFormatsToRender(creativeFormats, flight)

  const disableUpload = Boolean(
    errors.duration || (isGtvUser && selectedContentCategoryId == null)
  )

  return (
    <Box
      sx={{
        width: '100%',
        padding: {
          xs: theme.space['16'], // Mobile: smaller padding
          sm: theme.space['20'], // Small tablets: medium padding
          md: 0 // Medium screens and up: no padding
        }
      }}
    >
      {header}
      {warningMessage && formatAlert}
      {creativesOutsideFlightDuration && timeframeAlert}
      <Snackbar
        open={openSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar({ open: false, message: '' })}
        message={openSnackbar.message}
      />
      <ConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: 0 })}
        title="Delete Creative Asset"
        message="Are you sure you want to delete this creative asset?"
        onConfirm={() => deleteCreativeMutation(deleteModal.id)}
        isLoading={isPendingDelete}
      />
      {creativeModal.open && (
        <AppModal
          open={creativeModal.open}
          onClose={() => handleCloseModal()}
          contentRef={modalContentRef}
          header={
            <Typography variant="heading-4">
              {creativeModal.creativeId
                ? 'Edit Creative Asset'
                : 'Upload Creative Asset'}
            </Typography>
          }
          footer={
            !uploadPending &&
            !isPendingUpdate &&
            !isPendingCreate && (
              <Box
                display="flex"
                gap={theme.space['16']}
                sx={{
                  flexDirection: {
                    xs: 'column', // Mobile: stack buttons vertically
                    sm: 'row'      // Small screens and up: horizontal layout
                  },
                  width: '100%',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleCloseModal()}
                  sx={buttonStyles}
                >
                  <Typography variant="ui-3">Close</Typography>
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    !watchedName ||
                    Boolean(errors.name || errors.duration) ||
                    (!creativeModal.creativeId && !preparedFile)
                  }
                  onClick={() => {
                    if (creativeModal.creativeId) {
                      updateCreativeMutation({
                        id: creativeModal.creativeId ?? 0,
                        name: watchedName,
                        starts_at: dateTouched.startTouched
                          ? (watchedDuration?.starts_at
                              ? pacificDateStringToUtcStartOfDay(watchedDuration.starts_at)
                              : null)
                          : initialDuration.starts_at,
                        ends_at: dateTouched.endTouched
                          ? (watchedDuration?.ends_at
                              ? pacificDateStringToUtcEndOfDay(watchedDuration.ends_at)
                              : null)
                          : initialDuration.ends_at,
                        content_category_id: selectedContentCategoryId as number
                      })
                    } else if (preparedFile) {
                      const durationMs =
                        mediaType === 'image'
                          ? selectedDuration * 1000
                          : preparedMediaLength
                      const startsAt = watchedDuration?.starts_at
                        ? pacificDateStringToUtcStartOfDay(watchedDuration.starts_at)
                        : null
                      const endsAt = watchedDuration?.ends_at
                        ? pacificDateStringToUtcEndOfDay(watchedDuration.ends_at)
                        : null
                      const fields: FileUploadFields = {
                        name: watchedName,
                        aspectRatio: selectedAspectRatio?.ratio ?? '',
                        startsAt,
                        endsAt,
                        durationMs,
                        contentCategoryId: selectedContentCategoryId as number
                      }
                      createCreativeMutation({
                        flightId: flight.id,
                        fields,
                        file: preparedFile
                      })
                    }
                  }}
                  sx={buttonStyles}
                >
                  <Typography variant="ui-3">Save</Typography>
                </Button>
              </Box>
            )
          }
          content={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: responsiveGaps,
                marginTop: responsiveMargins
              }}
            >
              {uploadError.error && (
                <Alert severity="error">{uploadError.errorMessage}</Alert>
              )}
              {!uploadPending && !isPendingUpdate && (
                <FormProvider {...formMethods}>
                  <Controller
                    control={formMethods.control}
                    name="name"
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <InputLabel
                          id="name-label"
                          sx={{
                            marginBottom: theme.space['8'],
                            color:
                              'name' in errors
                                ? theme.palette['red-500']
                                : labelTextColor
                          }}
                        >
                          <Typography
                            variant="body-11"
                            sx={{ fontWeight: 500 }}
                          >
                            Creative Name
                          </Typography>
                        </InputLabel>
                        <TextField
                          {...field}
                          placeholder="Enter a name"
                          sx={{
                            width: '100%',
                            ...textFieldFontSizes
                          }}
                        />
                        <Typography
                          variant="body-11"
                          sx={{
                            fontStyle: 'italic',
                            color: theme.palette['sage-600'],
                            marginTop: theme.space['4'],
                            fontSize: '12px',
                            display: 'block'
                          }}
                        >
                          Optional: defaults to uploaded file name
                        </Typography>
                        {errors.name && (
                          <InputErrorMessage error={errors.name.message} />
                        )}
                      </Box>
                    )}
                  />
                  <FlightDates
                    context="creative"
                    flightStart={durationStart}
                    flightEnd={durationEnd}
                    hasEnded={creativeStatus.hasEnded}
                    onDateTouchedChange={(flags) => setDateTouched((prev) => ({ ...prev, ...flags }))}
                  />
                </FormProvider>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.space['12']
                }}
              >
                {isGtvUser || campaignContentCategory && (
                  <InputLabel>
                    <Typography
                      variant="body-11"
                      sx={{ color: labelTextColor }}
                    >
                      Content Category
                    </Typography>
                  </InputLabel>
                  )}
                  {isGtvUser && (
                  <Autocomplete
                    value={
                      contentCategories?.find(
                        (category) => category.id === selectedContentCategoryId
                      ) || null
                    }
                    options={(() => {
                      if (!childContentCategories) return []

                      const campaignParentId = getParentCategoryId(campaignContentCategory)
                      if (!campaignParentId) return childContentCategories
                      // Sort so campaign's parent group comes first
                      return [...childContentCategories].sort((a, b) => {
                        const aIsInCampaignGroup = a.parent_id === campaignParentId
                        const bIsInCampaignGroup = b.parent_id === campaignParentId

                        if (aIsInCampaignGroup && !bIsInCampaignGroup) return -1
                        if (!aIsInCampaignGroup && bIsInCampaignGroup) return 1
                        return 0
                      })
                    })()}
                    groupBy={(option) => getParentCategoryName(option, contentCategories)}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={(options, { inputValue }) => {
                      const searchTerm = inputValue.toLowerCase()
                      return options.filter((category) => {
                        if (category.name.toLowerCase().includes(searchTerm)) return true

                        const parentName = getParentCategoryName(category, contentCategories)
                        return parentName.toLowerCase().includes(searchTerm)
                      })
                    }}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <ListSubheader
                          component="div"
                          sx={{
                            backgroundColor: theme.palette['sage-100'],
                            fontWeight: 600,
                            color: theme.palette['sage-900'],
                            lineHeight: '32px',
                            fontSize: '14px'
                          }}
                        >
                          {params.group}
                        </ListSubheader>
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    )}
                    renderOption={(props, option) => {
                      const liProps = props as React.HTMLAttributes<HTMLLIElement> & { key?: string }
                      const optionKey = liProps.key
                      const restProps = { ...liProps } as React.HTMLAttributes<HTMLLIElement> & { key?: string }
                      delete restProps.key
                      return (
                        <MenuItem key={optionKey} {...(restProps as React.HTMLAttributes<HTMLLIElement>)}>
                          {option.name}
                        </MenuItem>
                      )
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(event, value) => {
                      setSelectedContentCategoryId(value?.id ?? null)
                      setUserChangedContentCategory(true)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Content Category"
                        sx={textFieldFontSizes}
                      />
                    )}
                    freeSolo={false}
                    autoComplete={false}
                    clearOnBlur
                    clearOnEscape
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette['sage-200']}`
                      }
                    }}
                    data-testid="content-category-input"
                  />
                  )}
                  {!isGtvUser && campaignContentCategory && (
                    <TextField
                      value={campaignContentCategory.name}
                      placeholder="Select Content Category"
                      disabled
                      sx={textFieldFontSizes}
                    />
                  )}
              </Box>
              <ContentCategoryMismatchBadge
                creativeContentCategory={selectedContentCategory ?? null}
                campaignContentCategory={campaignContentCategory ?? null}
                allContentCategories={contentCategories}
              />
              {!uploadPending && !creativeModal.creativeId && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <InputLabel>
                    <Typography
                      variant="body-11"
                      sx={{ color: labelTextColor }}
                    >
                      Media Type
                    </Typography>
                  </InputLabel>
                  <CreativeTypeSelector
                    maxDuration={
                      selectedAspectRatio?.max_creative_duration_ms ?? 0
                    }
                    mediaType={mediaType}
                    onMediaTypeChange={setMediaType}
                    selectedDuration={selectedDuration}
                    setSelectedDuration={setSelectedDuration}
                  />
                  <Box sx={{ width: '100%' }}>
                    <AssetUploader
                      accept=".png, .jpg, .jpeg, .mp4"
                      disabled={disableUpload}
                      orientation={selectedAspectRatio?.orientation ?? ''}
                      maxDuration={
                        mediaType === 'image'
                          ? selectedDuration
                          : selectedAspectRatio?.max_creative_duration_ms ?? 0
                      }
                      aspectRatio={selectedAspectRatio?.ratio ?? ''}
                      duration={{
                        name: watchedName,
                        duration: watchedDuration
                      }}
                      flightId={flight.id}
                      existingCreativeNames={flight.creatives.map((c) => c.name)}
                      onSuggestName={(suggested) => {
                        // Only set default if user hasn't typed a name yet
                        const currentName = formMethods.getValues('name')
                        if (!currentName) {
                          setValue('name', suggested)
                          clearErrors('name')
                        }
                      }}
                      onFilePrepared={({ file, mediaLength }) => {
                        setPreparedFile(file)
                        setPreparedMediaLength(mediaLength)
                        // Create preview URL for UX (optional)
                        if (previewUrl) URL.revokeObjectURL(previewUrl)
                        const url = URL.createObjectURL(file)
                        setPreviewUrl(url)
                      }}
                      flightTimezone={time_zone}
                      onUploadSuccess={(success: boolean) => {
                        if (success) {
                          setCreativeModal({
                            open: false,
                            creativeId: undefined
                          })
                          setUploadPending(false)
                          setUploadError({ error: false, errorMessage: '' })
                          saveTargetsMutation && saveTargetsMutation()
                        }
                      }}
                      onUploadPending={(isPending: boolean) => {
                        setUploadPending(isPending)
                      }}
                      onUploadError={({
                        error,
                        errorMessage
                      }: {
                        error: boolean
                        errorMessage: string
                      }) => {
                        setUploadPending(false)
                        setUploadError({ error, errorMessage })
                      }}
                    />
                    {preparedFile && previewUrl && (
                      <Box
                        ref={setPreviewRef}
                        sx={{
                          marginTop: theme.space['24'],
                        }}
                      >
                        <InputLabel>
                          <Typography
                            variant="body-11"
                            sx={{ color: labelTextColor }}
                          >
                            Creative Preview
                          </Typography>
                        </InputLabel>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            marginTop: theme.space['8']
                          }}
                        >
                          <Box
                            sx={{
                              borderRadius: theme.borders['rounded-8'],
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}
                          >
                            {preparedFile.type.startsWith('image/') ? (
                              <img
                                src={previewUrl}
                                alt="Creative preview"
                                style={mediaPreviewStyles}
                              />
                            ) : (
                              <video
                                src={previewUrl}
                                controls
                                playsInline
                                style={mediaPreviewStyles}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              {(uploadPending || isPendingUpdate || isPendingCreate) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <CircularProgress
                    sx={{ color: theme.palette['accent-500'] }}
                  />
                </Box>
              )}
            </Box>
          }
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: responsiveGaps
        }}
      >
        {formatsToRender.map((option) => (
          <Box key={option.orientation}>
            <CreativeOptionHeader
              option={option}
              handleSelectUpload={handleSelectUpload}
            />
            <Box
              sx={{
                marginTop: theme.space['16'],
                paddingLeft: {
                  xs: theme.space['24'], // Mobile: smaller left padding
                  sm: theme.space['32'], // Small tablets: medium left padding
                  md: theme.space['48']  // Medium screens and up: full left padding
                },
                borderLeft: `1px solid ${theme.palette['sage-200']}`
              }}
            >
              {flight.creatives.map((creative) => {
                const creativeOrientation = creative.assets.find(
                  (asset) => asset.type === 'video'
                )?.aspect_ratio.orientation

                if (option.orientation === creativeOrientation) {
                  return (
                    <CreativeDetails
                      key={creative.id}
                      creative={creative}
                      flight={flight}
                      campaignStatus={campaignStatus}
                      campaignContentCategory={campaignContentCategory}
                      allContentCategories={contentCategories}
                      onDelete={() =>
                        setDeleteModal({ open: true, id: creative.id })
                      }
                      canDelete={canDeleteCreative(creative)}
                      onEdit={() => handleEditCreative(creative)}
                    />
                  )
                }
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default FlightCreative
